import React, { useState } from 'react';
import { Send, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

// Endpoint FormSubmit (aucun backend requis). L'adresse doit être activée une
// première fois via l'email de confirmation FormSubmit (action manuelle).
const FORMSUBMIT_ENDPOINT = 'https://formsubmit.co/frelon33000@gmail.com';
const THANK_YOU_URL = 'https://www.frelon33.com/merci';

const PEST_TYPES = [
  'Frelon asiatique',
  'Frelon européen',
  'Guêpe',
  'Nid indéterminé',
  'Autre nuisible',
];

const InterventionForm = () => {
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    email: '',
    commune: '',
    type_nuisible: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isValid =
    formData.prenom.trim() &&
    formData.nom.trim() &&
    formData.telephone.trim() &&
    formData.commune.trim() &&
    formData.type_nuisible.trim() &&
    formData.description.trim();

  const handleSubmit = (e) => {
    if (submitting) {
      e.preventDefault();
      return;
    }
    if (!isValid) {
      e.preventDefault();
      setShowError(true);
      return;
    }
    // Soumission native vers FormSubmit -> redirection vers /merci (où
    // l'événement lead_form_success sera envoyé à GTM). On désactive le
    // bouton pour éviter les doubles soumissions.
    setShowError(false);
    setSubmitting(true);
  };

  return (
    <form
      action={FORMSUBMIT_ENDPOINT}
      method="POST"
      onSubmit={handleSubmit}
      className="frelon-card border-0 frelon-depth-3 space-y-6 text-left"
      data-testid="intervention-form"
      noValidate
    >
      {/* Paramètres FormSubmit */}
      <input type="hidden" name="_subject" value="Nouvelle demande d'intervention — Frelon33™" />
      <input type="hidden" name="_next" value={THANK_YOU_URL} />
      <input type="hidden" name="_template" value="table" />
      <input type="hidden" name="_captcha" value="false" />
      {/* Honeypot anti-spam FormSubmit */}
      <input type="text" name="_honey" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="prenom" className="text-gray-700 font-semibold">
            Prénom <span className="text-red-500">*</span>
          </Label>
          <Input
            id="prenom"
            name="prenom"
            type="text"
            value={formData.prenom}
            onChange={(e) => handleChange('prenom', e.target.value)}
            placeholder="Votre prénom"
            required
            data-testid="form-input-prenom"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="nom" className="text-gray-700 font-semibold">
            Nom <span className="text-red-500">*</span>
          </Label>
          <Input
            id="nom"
            name="nom"
            type="text"
            value={formData.nom}
            onChange={(e) => handleChange('nom', e.target.value)}
            placeholder="Votre nom"
            required
            data-testid="form-input-nom"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="telephone" className="text-gray-700 font-semibold">
            Téléphone <span className="text-red-500">*</span>
          </Label>
          <Input
            id="telephone"
            name="telephone"
            type="tel"
            inputMode="tel"
            value={formData.telephone}
            onChange={(e) => handleChange('telephone', e.target.value)}
            placeholder="06 XX XX XX XX"
            required
            data-testid="form-input-telephone"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-gray-700 font-semibold">
            Email (optionnel)
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="votre@email.com"
            data-testid="form-input-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="commune" className="text-gray-700 font-semibold">
            Commune / lieu d'intervention <span className="text-red-500">*</span>
          </Label>
          <Input
            id="commune"
            name="commune"
            type="text"
            value={formData.commune}
            onChange={(e) => handleChange('commune', e.target.value)}
            placeholder="Ex : Cadaujac, Bordeaux 33000"
            required
            data-testid="form-input-commune"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="type_nuisible" className="text-gray-700 font-semibold">
            Type de nuisible <span className="text-red-500">*</span>
          </Label>
          <select
            id="type_nuisible"
            name="type_nuisible"
            value={formData.type_nuisible}
            onChange={(e) => handleChange('type_nuisible', e.target.value)}
            required
            data-testid="form-select-type"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="" disabled>Sélectionnez…</option>
            {PEST_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description" className="text-gray-700 font-semibold">
          Description <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Décrivez la situation : localisation du nid, taille, hauteur, urgence…"
          rows={4}
          required
          data-testid="form-input-description"
        />
      </div>

      {showError && !isValid && (
        <div
          className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg flex items-start gap-3"
          role="alert"
          data-testid="form-error-message"
        >
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 leading-relaxed">
            Merci de remplir tous les champs obligatoires (*).
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="frelon-btn frelon-btn-primary w-full text-lg px-8 py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
        data-testid="form-submit-button"
        data-gtm-event="lead_form_submit"
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Envoi de votre demande…</span>
          </>
        ) : (
          <>
            <Send className="h-5 w-5" />
            <span>Envoyer ma demande d'intervention</span>
          </>
        )}
      </button>

      <p className="text-sm text-gray-500 text-center leading-relaxed">
        En envoyant ce formulaire, vous acceptez d'être recontacté par{' '}
        <span className="frelon33-brand font-semibold">Frelon33™</span> au sujet de votre demande.
      </p>
    </form>
  );
};

export default InterventionForm;
